import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { matchesTextQuery } from "@/lib/textSearch";
import {
  Button,
  Modal,
  ModalFooter,
  PercentInput,
} from "@/components/ui";
import { PickerPanel } from "@/components/common";
import { rateFromPercentInput } from "@/lib/commissionRate";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import { queryLoadFailure } from "@/lib/queryLoadFailure";

export function AssignmentModal<TItem>({
  assignedIds,
  isPending,
  cancelLabel,
  saveLabel,
  onClose,
  onSave,
  queryKey,
  fetchItems,
  getItemId,
  getTitle,
  getSubtitle,
  labels,
}: {
  assignedIds: Set<string>;
  isPending: boolean;
  cancelLabel: string;
  saveLabel: string;
  onClose: () => void;
  onSave: (entityId: string, commissionPct: string) => void;
  queryKey: readonly unknown[];
  fetchItems: (search: string) => Promise<TItem[]>;
  getItemId: (item: TItem) => string;
  getTitle: (item: TItem, language: string) => string;
  getSubtitle: (item: TItem) => string;
  labels: {
    title: string;
    description: string;
    stepLabel: string;
    searchPlaceholder: string;
    emptyMessage: string;
    loadErrorTitle: string;
    commissionLabel: string;
  };
}) {
  const { i18n } = useTranslation("salesAgent");
  const { t: tCommon } = useTranslation("common");
  const [search, setSearch] = useState("");
  const [selectedId, setSelectedId] = useState("");
  const [commissionPct, setCommissionPct] = useState("");
  const debouncedSearch = useDebouncedValue(search, 300);
  const trimmedSearch = search.trim();
  const trimmedDebouncedSearch = debouncedSearch.trim();

  const itemsQuery = useQuery({
    queryKey: [...queryKey, trimmedDebouncedSearch],
    queryFn: () => fetchItems(trimmedDebouncedSearch),
    placeholderData: (previous) => previous,
  });
  const { data: items = [], isLoading, isFetching } = itemsQuery;

  const availableItems = useMemo(() => {
    return items.filter((item) => {
      if (assignedIds.has(getItemId(item))) return false;
      const title = getTitle(item, i18n.language);
      return matchesTextQuery(trimmedSearch, [title, getSubtitle(item)], {
        trim: false,
      });
    });
  }, [
    assignedIds,
    getItemId,
    getSubtitle,
    getTitle,
    i18n.language,
    items,
    trimmedSearch,
  ]);

  const isSearchRefreshing =
    trimmedSearch !== trimmedDebouncedSearch || (isFetching && !isLoading);

  const canSave =
    !!selectedId &&
    rateFromPercentInput(commissionPct.trim()) != null &&
    !isPending;

  return (
    <Modal
      closeLabel={tCommon("close")}
      isOpen
      onClose={onClose}
      title={labels.title}
      description={labels.description}
      size="lg"
      footer={
        <ModalFooter>
          <Button variant="outline" onClick={onClose} disabled={isPending}>
            {cancelLabel}
          </Button>
          <Button
            variant="primary"
            onClick={() => onSave(selectedId, commissionPct)}
            disabled={!canSave}
            isLoading={isPending}
          >
            {saveLabel}
          </Button>
        </ModalFooter>
      }
    >
      <PickerPanel
        stepLabel={labels.stepLabel}
        searchPlaceholder={labels.searchPlaceholder}
        searchValue={search}
        onSearchChange={setSearch}
        isLoading={isLoading}
        isRefreshing={isSearchRefreshing}
        loadError={queryLoadFailure(itemsQuery, labels.loadErrorTitle)}
        emptyMessage={labels.emptyMessage}
        items={availableItems.map((item) => {
          const id = getItemId(item);
          return {
            id,
            title: getTitle(item, i18n.language),
            subtitle: getSubtitle(item),
            selected: selectedId === id,
            onSelect: () => setSelectedId(id),
          };
        })}
      />
      <div className="mt-4 max-w-xs">
        <PercentInput
          label={labels.commissionLabel}
          value={commissionPct}
          onChange={(e) => setCommissionPct(e.target.value)}
          required
        />
      </div>
    </Modal>
  );
}
